import '../styles/modules-manual-bank-balance.css';
import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useRef, useState } from 'react';
import { financialOverviewApi } from './financial-overview-api';

/** Integer conversion preserves zeros and negatives without floating-point rounding. */
export function manualBalanceCents(value: string): string | null {
  const match = /^(-?)(\d{1,13})(?:[.,](\d{1,2}))?$/u.exec(value.trim());
  if (!match) return null;
  return (
    (BigInt(match[2]!) * 100n + BigInt((match[3] ?? '').padEnd(2, '0'))) *
    (match[1] === '-' ? -1n : 1n)
  ).toString();
}
function localNow(): string {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export function ManualBankBalanceDialog({
  csrfToken,
  onClose,
  onSaved,
}: {
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const [amount, setAmount] = useState('');
  const [asOf, setAsOf] = useState(localNow);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <ModalDialog
      ref={ref}
      className="manual-bank-balance-dialog"
      aria-labelledby="manual-balance-title"
      onCancel={(event) => {
        if (saving) event.preventDefault();
        else onClose();
      }}
      close={
        <ModalCloseButton
          className="manual-bank-balance__close"
          aria-label="Fermer"
          disabled={saving}
          onClick={onClose}
        />
      }
    >
      <form
        className="manual-bank-balance"
        onSubmit={(event) => {
          event.preventDefault();
          const cents = manualBalanceCents(amount);
          const date = new Date(asOf);
          if (cents === null || !Number.isFinite(date.getTime()) || date > new Date()) {
            setError('Saisissez un solde exact et une date qui ne soit pas dans le futur.');
            return;
          }
          if (saving) return;
          setSaving(true);
          setError(null);
          void financialOverviewApi
            .saveManualBankBalance(cents, date.toISOString(), csrfToken)
            .then(onSaved)
            .catch(() => setError('Le solde de secours n’a pas pu être enregistré.'))
            .finally(() => setSaving(false));
        }}
      >
        <header className="tosho-modal-heading tosho-modal-header">
          <h2 id="manual-balance-title">Solde manuel de secours</h2>
        </header>
        <div className="manual-bank-balance__body">
          <p>
            À utiliser uniquement en cas de panne Freebe ou bancaire. Freebe reste la source
            principale et reprend automatiquement dès qu’une synchronisation bancaire plus récente
            est disponible.
          </p>
          <label>
            Solde bancaire (€)
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              placeholder="0,00"
              value={amount}
              disabled={saving}
              onChange={(event) => setAmount(event.currentTarget.value)}
              required
            />
          </label>
          <label>
            Date et heure du solde
            <input
              type="datetime-local"
              value={asOf}
              disabled={saving}
              onChange={(event) => setAsOf(event.currentTarget.value)}
              required
            />
          </label>
          <p>Ce montant sera identifié comme manuel ; aucune transaction bancaire ne sera créée.</p>
          {error ? (
            <p className="tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer le solde de secours'}
          </button>
        </div>
      </form>
    </ModalDialog>
  );
}
